import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Users, User, ArrowLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const Signup = () => {
  const [step, setStep] = useState(1);
  const [userType, setUserType] = useState("");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Check if user is already logged in
    const checkUser = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session) {
        navigate("/");
      }
    };
    checkUser();

    // Check for canceled payment
    if (searchParams.get("canceled") === "true") {
      toast({
        title: "Payment Canceled",
        description:
          "Your subscription payment was canceled. You can subscribe from your dashboard.",
        variant: "destructive",
      });
    }
  }, [navigate, searchParams, toast]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validatePassword = (password: string) => {
    const minLength = 8;
    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasDigits = /\d/.test(password);
    const hasSymbols = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

    if (password.length < minLength) {
      return "Password must be at least 8 characters long";
    }
    if (!hasLowercase) {
      return "Password must contain at least one lowercase letter";
    }
    if (!hasUppercase) {
      return "Password must contain at least one uppercase letter";
    }
    if (!hasDigits) {
      return "Password must contain at least one number";
    }
    if (!hasSymbols) {
      return "Password must contain at least one symbol (!@#$%^&*()_+-=[]{}|;:,.<>?)";
    }
    return null;
  };

  const validateStep2 = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else {
      const passwordError = validatePassword(formData.password);
      if (passwordError) {
        newErrors.password = passwordError;
      }
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (step === 1 && userType) {
      setStep(2);
    } else if (step === 2) {
      if (validateStep2()) {
        handleSignup();
      }
    }
  };

  const handleBackStep = () => {
    if (step > 1) {
      setStep(step - 1);
      setErrors({});
    }
  };

  const handleSignup = async () => {
    setLoading(true);

    try {
      // Determine redirect URL based on user type
      const redirectUrl = userType === 'breeder' 
        ? `${window.location.origin}/breeder-dashboard`
        : `${window.location.origin}/buyer-dashboard`;

      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            user_type: userType,
            first_name: formData.firstName,
            last_name: formData.lastName,
          },
        },
      });

      if (error) {
        // Handle specific error cases
        if (
          error.message.includes("already registered") ||
          error.message.includes("already been registered")
        ) {
          setErrors({
            email:
              "This email is already registered. Please use a different email or try logging in.",
          });
          toast({
            title: "Email Already Registered",
            description:
              "This email is already in use. Please try logging in instead.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Signup Failed",
            description: error.message,
            variant: "destructive",
          });
        }
        return;
      }

      if (data.user) {
        // Redirect to thank you page
        navigate("/signup-thank-you");
      }
    } catch (error) {
      toast({
        title: "Signup Failed",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center px-4">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-green-100 to-teal-50"></div>

      {/* Gradient overlay with better contrast */}
      <div className="absolute inset-0 bg-gradient-to-b from-green-600/15 via-emerald-500/10 to-teal-600/15"></div>

      {/* Gradient orbs for depth */}
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-gradient-to-r from-green-400/30 to-emerald-400/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute top-1/4 right-1/3 w-72 h-72 bg-gradient-to-r from-teal-300/25 to-green-300/35 rounded-full blur-2xl"></div>
      <div className="absolute -bottom-24 -right-24 w-80 h-80 bg-gradient-to-r from-emerald-400/25 to-teal-400/30 rounded-full blur-3xl"></div>

      {/* Accent orb */}
      <div className="absolute top-3/4 left-1/5 w-48 h-48 bg-gradient-to-r from-green-200/40 to-emerald-200/30 rounded-full blur-2xl"></div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <h1 className="text-4xl font-extrabold">
              <span className="text-black">ur</span>
              <span className="text-green-800">Cattle</span>
            </h1>
          </Link>
          <p className="text-gray-600 mt-2">
            Join the premium cattle marketplace
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-center flex items-center justify-between">
              {step > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBackStep}
                  disabled={loading}
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
              )}
              <span className="flex-1">
                {step === 1 && "Choose Account Type"}
                {step === 2 && "Create Account"}
              </span>
              {step > 1 && <div className="w-8"></div>}
            </CardTitle>
            <div className="flex justify-center space-x-2 mt-4">
              {[1, 2].map((i) => (
                <div
                  key={i}
                  className={`w-3 h-3 rounded-full ${
                    i <= step ? "bg-green-700" : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </CardHeader>
          <CardContent>
            {step === 1 && (
              <div className="space-y-6">
                <RadioGroup value={userType} onValueChange={setUserType}>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                      <RadioGroupItem value="breeder" id="breeder" />
                      <Label
                        htmlFor="breeder"
                        className="flex items-center cursor-pointer flex-1"
                      >
                        <Users className="w-8 h-8 text-green-700 mr-3" />
                        <div>
                          <div className="font-medium">Breeder</div>
                          <div className="text-sm text-gray-600">
                            List and Start Your Cattle Brand Today
                          </div>
                        </div>
                      </Label>
                    </div>

                    <div className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                      <RadioGroupItem value="buyer" id="buyer" />
                      <Label
                        htmlFor="buyer"
                        className="flex items-center cursor-pointer flex-1"
                      >
                        <User className="w-8 h-8 text-blue-700 mr-3" />
                        <div>
                          <div className="font-medium">Buyer</div>
                          <div className="text-sm text-gray-600">
                            Browse and Connect with Cattle Breeders
                          </div>
                          <div className="text-xs text-blue-700 font-medium">
                            Free to join
                          </div>
                        </div>
                      </Label>
                    </div>
                  </div>
                </RadioGroup>

                <Button
                  onClick={handleNextStep}
                  className="w-full bg-green-700 hover:bg-green-800"
                  disabled={!userType}
                >
                  Continue
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  By signing up, you agree to our{" "}
                  <Link
                    to="/terms"
                    className="text-green-700 hover:text-green-800 underline"
                  >
                    Terms of Use
                  </Link>{" "}
                  and{" "}
                  <Link
                    to="/privacy"
                    className="text-green-700 hover:text-green-800 underline"
                  >
                    Privacy Policy
                  </Link>
                </p>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder="John"
                      required
                      className={errors.firstName ? "border-red-500" : ""}
                    />
                    {errors.firstName && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.firstName}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      placeholder="Doe"
                      required
                      className={errors.lastName ? "border-red-500" : ""}
                    />
                    {errors.lastName && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.lastName}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Email address</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="john@example.com"
                    required
                    className={errors.email ? "border-red-500" : ""}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Create a strong password"
                    required
                    className={errors.password ? "border-red-500" : ""}
                  />
                  {errors.password && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.password}
                    </p>
                  )}
                  <div className="text-xs text-gray-500 mt-1">
                    Password must contain: lowercase, uppercase, numbers, and
                    symbols (8+ characters)
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Confirm your password"
                    required
                    className={errors.confirmPassword ? "border-red-500" : ""}
                  />
                  {errors.confirmPassword && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>

                <Button
                  onClick={handleNextStep}
                  className="w-full bg-green-700 hover:bg-green-800"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating Account...
                    </div>
                  ) : (
                    "Create Account"
                  )}
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  By signing up, you agree to our{" "}
                  <Link
                    to="/terms"
                    className="text-green-700 hover:text-green-800 underline"
                  >
                    Terms of Use
                  </Link>{" "}
                  and{" "}
                  <Link
                    to="/privacy"
                    className="text-green-700 hover:text-green-800 underline"
                  >
                    Privacy Policy
                  </Link>
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <p className="text-gray-600">
            Already have an account?{" "}
            <Link
              to="/login"
              className="text-green-700 hover:text-green-800 font-medium"
            >
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
